import { createColumnHelper } from '@tanstack/react-table';
import { SettingsUsersTableMeta } from '@app/types/overrides/table-meta';
import MainTableRowActions from '@components/MainTable/MainTableRowActions';
import { CompanyUserStatus } from '@utils/enums.ts';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {
  IconTrash,
  IconUserCheck,
  IconUserMinus,
  IconSend,
  IconUserShield,
  IconScale,
  IconScaleOff,
} from '@tabler/icons-react';
import AnimateButton from '@components/extended/AnimateButton';
import { CompanyMember } from '@app/types/user/company';
import { Typography } from '@mui/material';
import CompanyUserAdminStatusTag from '@features/settings-users/components/CompanyUserAdminStatusTag';
import CompanyUserStatusTag from '@features/settings-users/components/CompanyUserStatusTag';

const columnHelper = createColumnHelper<CompanyMember>();

const createSettingUsersColumns = () => {
  return [
    columnHelper.accessor('user_name', {
      id: 'user_name',
      header: 'Name',
      enableSorting: true,
      enableResizing: false,
      size: 100,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">{original.user_name}</Typography>
      ),
    }),
    columnHelper.accessor('company_name', {
      id: 'company_name',
      header: 'Company Name',
      enableSorting: true,
      enableResizing: false,
      size: 100,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">
          {original.company_name || 'Individual User'}
        </Typography>
      ),
    }),
    columnHelper.accessor('user_email', {
      id: 'user_email',
      header: 'Email',
      size: 200,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <Typography variant="body2">{original.user_email}</Typography>
      ),
    }),
    columnHelper.accessor('is_company_admin', {
      id: 'is_company_admin',
      header: 'Admin',
      size: 100,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <CompanyUserAdminStatusTag
          is_company_admin={original.is_company_admin}
        />
      ),
    }),
    columnHelper.accessor('is_attorney', {
      id: 'is_attorney',
      header: 'Lawyer',
      size: 100,
      enableSorting: true,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <Typography
          variant="body2"
          color={original.is_attorney ? 'text.primary' : 'text.secondary'}
        >
          {original.is_attorney ? 'Lawyer' : 'No'}
        </Typography>
      ),
    }),
    columnHelper.accessor('company_user_status', {
      id: 'company_user_status',
      header: 'Status',
      enableSorting: true,
      size: 100,
      enableResizing: false,
      cell: ({ row: { original } }) => (
        <CompanyUserStatusTag status={original.company_user_status} />
      ),
    }),
    columnHelper.display({
      id: 'id',
      header: 'Actions',
      enableSorting: false,
      enableResizing: false,
      cell: ({
        row: { original },
        table: {
          options: { meta },
        },
      }) => {
        const tableMeta = meta as SettingsUsersTableMeta<typeof original>;
        const canActivateUser = [
          CompanyUserStatus.AccountPendingApproval,
          CompanyUserStatus.Inactive,
        ].includes(original.company_user_status as CompanyUserStatus);

        return (
          <MainTableRowActions>
            <>
              {original.company_user_status === CompanyUserStatus.Active && (
                <>
                  <AnimateButton>
                    <Tooltip title="Deactivate user" arrow>
                      <IconButton
                        onClick={() => {
                          tableMeta?.handleDeactivateUser &&
                            tableMeta?.handleDeactivateUser(original);
                        }}
                      >
                        <IconUserMinus />
                      </IconButton>
                    </Tooltip>
                  </AnimateButton>
                </>
              )}
              {canActivateUser && (
                <AnimateButton>
                  <Tooltip title="Activate user" arrow>
                    <IconButton
                      onClick={() => {
                        tableMeta?.handleActivateUser &&
                          tableMeta?.handleActivateUser(original);
                      }}
                    >
                      <IconUserCheck />
                    </IconButton>
                  </Tooltip>
                </AnimateButton>
              )}
              {original.company_user_status === CompanyUserStatus.Inactive && (
                <>
                  <AnimateButton>
                    <Tooltip title="Resend invitation" arrow>
                      <IconButton
                        onClick={() => {
                          tableMeta?.handleResendInvitation &&
                            tableMeta?.handleResendInvitation(original);
                        }}
                      >
                        <IconSend />
                      </IconButton>
                    </Tooltip>
                  </AnimateButton>
                </>
              )}
              <AnimateButton>
                <Tooltip
                  title={
                    original.is_company_admin ? 'Remove Admin' : 'Make Admin'
                  }
                >
                  <IconButton
                    onClick={() => {
                      tableMeta?.handleUpdateMemberAdminRole &&
                        tableMeta?.handleUpdateMemberAdminRole(original);
                    }}
                  >
                    {original.is_company_admin ? (
                      <IconUserMinus />
                    ) : (
                      <IconUserShield />
                    )}
                  </IconButton>
                </Tooltip>
              </AnimateButton>
              {original.company_user_status === CompanyUserStatus.Active && (
                <AnimateButton>
                  <Tooltip
                    title={
                      original.is_attorney ? 'Remove lawyer' : 'Mark as lawyer'
                    }
                  >
                    <IconButton
                      aria-label={
                        original.is_attorney
                          ? 'Remove lawyer'
                          : 'Mark as lawyer'
                      }
                      onClick={() => {
                        tableMeta?.handleUpdateMemberAttorneyRole &&
                          tableMeta?.handleUpdateMemberAttorneyRole(original);
                      }}
                    >
                      {original.is_attorney ? <IconScaleOff /> : <IconScale />}
                    </IconButton>
                  </Tooltip>
                </AnimateButton>
              )}
              <AnimateButton>
                <Tooltip title="Delete Company Member">
                  <IconButton
                    onClick={() => {
                      tableMeta?.handleDeleteRow &&
                        tableMeta?.handleDeleteRow(original);
                    }}
                  >
                    <IconTrash />
                  </IconButton>
                </Tooltip>
              </AnimateButton>
            </>
          </MainTableRowActions>
        );
      },
    }),
  ];
};

export default createSettingUsersColumns;
